import { SignInPage } from '@ohpen/react-auth';
import { useConfig } from '@ohpen/react-utils';
import { useAuth } from 'react-oidc-context';
import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getCurrentUser } from 'aws-amplify/auth';
import { PageSpinner } from '@ohpen/enso-ui';

export const Login = () => {
  const config = useConfig();
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const providerIds = config?.AWS?.IDENTITY_PROVIDERS || [];
  const providers = providerIds.map(id => ({ id, name: id, type: 'azure' as const }));

  const cdnUrl = config?.THEMING?.ASSETS_BASE_URL || 'https://cdn.portal.ohpen.tech';
  const client = config.CLIENT;
  const logoFile = config?.THEMING?.LOGO_FILE || 'logo-horizontal.png';
  const logoUrl = `${cdnUrl}/${client}/${logoFile}`;

  // Enable credentials login based on config.
  const enableCredentials = config?.AWS?.ENABLE_CREDENTIALS_LOGIN ?? false;
  const hasSSO = providers.length > 0;

  // The URL the user was trying to access before being redirected to login.
  // For SSO the destination is preserved via the OIDC state parameter, not sessionStorage.
  const routerFrom = (location.state as { from?: string } | null)?.from;
  const from = routerFrom || '/';

  // Show spinner while checking if already authenticated, to avoid a flash of the login form.
  // OIDC auth is async (loads from localStorage); Amplify credentials auth is also async.
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Wait until OIDC has finished loading before checking auth state.
    if (auth.isLoading) return;

    const checkAuth = async () => {
      // OIDC (SSO) is authenticated — navigate immediately.
      if (auth.isAuthenticated) {
        navigate(from, { replace: true });
        return;
      }

      // Check Amplify credentials auth.
      if (enableCredentials) {
        try {
          await getCurrentUser();
          navigate(from, { replace: true });
          return;
        } catch {
          // Not authenticated via credentials either — show the login form.
        }
      }

      setChecking(false);
    };

    checkAuth();
  }, [auth.isLoading, auth.isAuthenticated, enableCredentials, navigate, from]);

  if (checking) return <PageSpinner />;

  const handleProviderLogin = async (providerId: string) => {
    await auth.signinRedirect({
      extraQueryParams: { identity_provider: providerId },
      state: { from },
    });
  };

  const handleAfterSignIn = async () => {
    navigate(from, { replace: true });
  };

  // Determine description based on available login methods
  const getDescription = () => {
    if (hasSSO && enableCredentials) return 'Sign in using SSO or credentials';
    if (enableCredentials) return 'Sign in using your credentials';
    return 'Sign in using your corporate account';
  };

  return (
    <SignInPage
      logo={logoUrl}
      description={getDescription()}
      providers={providers}
      enableCredentials={enableCredentials}
      onProviderLogin={handleProviderLogin}
      onAfterSignIn={handleAfterSignIn}
      useAuthContext={false}
    />
  );
};
